/**
 * Provider Config Store - State management for recognition provider configuration
 * Manages per-device, per-dice-type provider priority settings (TICKET-167)
 */

import { create } from 'zustand';
import type { DiceType, ProviderConfigResponse, RecognitionProvider, AvailableProvider } from '@dice-capture/shared/types/dice-registry';
import { cognitoService } from '@/services/cognitoService';
import { API_BASE_URL, API_KEY } from '@/config/api';

const SERVICE_TAG = '[PROVIDER_CONFIG]';

interface ProviderConfigState {
  /** Provider configs keyed by `${deviceId}#${diceType}` */
  providerConfigs: Record<string, ProviderConfigResponse>;

  /**
   * Fetch provider configuration for a device + dice type combination.
   * @param deviceId - Device identifier
   * @param diceType - The dice type to fetch config for
   */
  fetchProviderConfig: (deviceId: string, diceType: DiceType) => Promise<void>;

  /**
   * Update active providers for a device + dice type combination.
   * @param deviceId - Device identifier
   * @param diceType - The dice type to update config for
   * @param activeProviders - Ordered list of active providers
   */
  updateProviderConfig: (deviceId: string, diceType: DiceType, activeProviders: RecognitionProvider[]) => Promise<void>;

  /**
   * Bulk-populate provider configs from a cross-device fetch.
   * Used by diceRegistryStore when loading via /my-registrations endpoint.
   */
  bulkSetConfigs: (configs: Array<{
    deviceId: string;
    diceType: string;
    activeProviders: string[];
    availableProviders: Array<{ id: string; label: string; trained: boolean; trainingStatus?: string }>;
  }>) => void;
}

export const useProviderConfigStore = create<ProviderConfigState>()((set, get) => ({
  providerConfigs: {},

  /**
   * Fetch provider configuration for a device + dice type combination (TICKET-167).
   */
  fetchProviderConfig: async (deviceId: string, diceType: DiceType) => {
    const apiBaseUrl = API_BASE_URL;
    const apiKey = API_KEY;
    const key = `${deviceId}#${diceType}`;

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Api-Key': apiKey
      };
      const idToken = await cognitoService.getIdToken();
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }

      const response = await fetch(
        `${apiBaseUrl}/dice-registry/devices/${deviceId}/${diceType}/providers`,
        { headers }
      );
      if (!response.ok) {
        throw new Error(`Provider config fetch failed: ${response.status}`);
      }
      const data: ProviderConfigResponse = await response.json();
      set((state) => ({
        providerConfigs: { ...state.providerConfigs, [key]: data }
      }));
      console.info(`${SERVICE_TAG} Provider config fetched`, { deviceId, diceType });
    } catch (error) {
      console.warn(`${SERVICE_TAG} Failed to fetch provider config`, {
        deviceId,
        diceType,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  },

  /**
   * Update active providers for a device + dice type combination (TICKET-167).
   *
   * Optimistically applies the new activeProviders to the local store before the
   * PUT round-trip. On success, re-fetches for canonical state (the backend may
   * silently strip auth-gated LLMs for guests). On error, re-fetches to revert
   * to whatever the server actually has. This keeps the UI snappy and avoids
   * iOS Safari / slow-network races where the post-PUT GET arrived after the
   * user had already moved on.
   */
  updateProviderConfig: async (deviceId: string, diceType: DiceType, activeProviders: RecognitionProvider[]) => {
    const apiBaseUrl = API_BASE_URL;
    const apiKey = API_KEY;
    const key = `${deviceId}#${diceType}`;

    // Optimistic local update — keeps the toggle visually in sync immediately.
    set((state) => {
      const existing = state.providerConfigs[key];
      if (!existing) return state;
      return {
        providerConfigs: {
          ...state.providerConfigs,
          [key]: { ...existing, activeProviders }
        }
      };
    });

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Api-Key': apiKey
      };
      const idToken = await cognitoService.getIdToken();
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }

      const response = await fetch(
        `${apiBaseUrl}/dice-registry/devices/${deviceId}/${diceType}/providers`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ activeProviders })
        }
      );
      if (!response.ok) {
        throw new Error(`Provider config update failed: ${response.status}`);
      }
      // Re-fetch for canonical state (e.g. backend may have silent-stripped a gated LLM).
      await get().fetchProviderConfig(deviceId, diceType);
      console.info(`${SERVICE_TAG} Provider config updated`, { deviceId, diceType, activeProviders });
    } catch (error) {
      console.error(`${SERVICE_TAG} Failed to update provider config`, {
        deviceId,
        diceType,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      // Revert optimistic update by re-fetching the true server state.
      await get().fetchProviderConfig(deviceId, diceType);
      throw error;
    }
  },

  /**
   * Bulk-populate provider configs from a cross-device fetch (TICKET-224).
   */
  bulkSetConfigs: (configs) => {
    const newConfigs: Record<string, ProviderConfigResponse> = {};
    for (const config of configs) {
      const key = `${config.deviceId}#${config.diceType}`;
      newConfigs[key] = {
        activeProviders: config.activeProviders as RecognitionProvider[],
        availableProviders: config.availableProviders as AvailableProvider[],
      };
    }
    set((state) => ({
      providerConfigs: { ...state.providerConfigs, ...newConfigs }
    }));
    console.info(`${SERVICE_TAG} Bulk configs loaded`, { count: configs.length });
  }
}));
